<script lang="ts">
  import Button from "../shared/inputs/button/Button.svelte";
  import Loader from "../shared/Loader.svelte";
  import type { TSubscription } from "@packages/utils/types";

  export let loadSubscriptions: () => Promise<TSubscription[]>;
  export let cancelSubscription: (id: string) => Promise<TSubscription>;

  let subscriptions: TSubscription[] = [];
  let isLoading = true;
  let errorMessage = "";
  let cancellingId = "";

  const shorten = (value?: string | number) => {
    if (value === undefined || value === null) return "";

    const text = String(value);
    if (text.length <= 16) return text;

    return `${text.slice(0, 8)}...${text.slice(-6)}`;
  };

  const formatItem = (item: NonNullable<TSubscription["items"]>[number]) => {
    if (typeof item === "string") return item;

    const directive = item.directive;
    if (!directive) return "";

    const type = directive.type || "subscription";
    const tokenId = directive.value?.token_id;
    const resourceId = directive.resource_id;
    const resourceLabel = resourceId ? ` ${shorten(resourceId)}` : "";
    const tokenLabel = tokenId !== undefined && tokenId !== null ? ` #${tokenId}` : "";

    return `${type}${tokenLabel}${resourceLabel}`;
  };

  const formatSubscriptionTitle = (subscription: TSubscription) => {
    const items = subscription.items?.map(formatItem).filter(Boolean);
    return subscription.description || items?.join(", ") || subscription._id;
  };

  const formatPrice = (subscription: TSubscription) => {
    if (subscription.price === undefined || subscription.price === null) return "";

    const amount = subscription.price > 100 ? subscription.price / 100 : subscription.price;
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: subscription.currency || "USD",
    }).format(amount);
  };

  const isCancelable = (subscription: TSubscription) => {
    const status = subscription.status?.toLowerCase();
    return status !== "canceled" && status !== "pending_cancel";
  };

  const refreshSubscriptions = async () => {
    try {
      isLoading = true;
      errorMessage = "";
      subscriptions = await loadSubscriptions();
    } catch (err) {
      errorMessage = err instanceof Error ? err.message : "Failed to load subscriptions";
    } finally {
      isLoading = false;
    }
  };

  const handleCancel = async (id: string) => {
    try {
      cancellingId = id;
      errorMessage = "";
      await cancelSubscription(id);
      await refreshSubscriptions();
    } catch (err) {
      errorMessage = err instanceof Error ? err.message : "Failed to cancel subscription";
    } finally {
      cancellingId = "";
    }
  };

  void refreshSubscriptions();
</script>

<div class="crtw-w-full crtw-text-left">
  <div class="crtw-font-bold crtw-text-xl crtw-mb-1">Subscriptions</div>
  <div class="crtw-text-sm crtw-text-gray-500 crtw-mb-5">Manage your active and past subscriptions.</div>

  {#if isLoading}
    <div class="crtw-flex crtw-justify-center crtw-py-10">
      <Loader />
    </div>
  {:else if errorMessage}
    <div class="crtw-rounded crtw-bg-red-50 crtw-text-red-700 crtw-text-sm crtw-p-3 crtw-mb-4">
      {errorMessage}
    </div>
    <Button text="Try again" onClick={refreshSubscriptions} />
  {:else if !subscriptions.length}
    <div class="crtw-rounded crtw-border crtw-border-gray-200 crtw-p-4 crtw-text-center crtw-text-gray-500 crtw-text-sm">
      You do not have any subscriptions yet.
    </div>
  {:else}
    <div class="crtw-flex crtw-flex-col crtw-gap-3">
      {#each subscriptions as subscription}
        <div class="crtw-rounded crtw-border crtw-border-gray-200 crtw-p-3">
          <div class="crtw-flex crtw-justify-between crtw-gap-3 crtw-items-start">
            <div class="crtw-min-w-0">
              <div class="crtw-font-semibold crtw-break-words">
                {formatSubscriptionTitle(subscription)}
              </div>
              <div class="crtw-text-xs crtw-uppercase crtw-tracking-wide crtw-text-gray-500 crtw-mt-1">
                {subscription.status}
              </div>
            </div>
            {#if formatPrice(subscription)}
              <div class="crtw-font-semibold crtw-whitespace-nowrap">{formatPrice(subscription)}</div>
            {/if}
          </div>

          {#if subscription.error}
            <div class="crtw-text-xs crtw-text-red-600 crtw-mt-2">{subscription.error}</div>
          {/if}

          {#if isCancelable(subscription)}
            <div class="crtw-mt-3">
              <Button
                text="Cancel subscription"
                onClick={() => handleCancel(subscription._id)}
                isLoading={cancellingId === subscription._id}
                disabled={!!cancellingId}
              />
            </div>
          {/if}
        </div>
      {/each}
    </div>
  {/if}
</div>
